/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import CryptoJS from 'crypto-js';

/**
 * AES encryption matching backend Aegis.Common.Cryptography.Aes
 *
 * Backend algorithm:
 *  - AES-128-CBC, PKCS7 padding
 *  - IV: { 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16 }
 *  - Key: MD5( Encoding.Unicode.GetBytes(key) )  — Unicode = UTF-16LE in .NET
 *  - Plaintext: Encoding.Unicode.GetBytes(text)   — UTF-16LE
 *  - Output: Base64
 */

// IV matching backend: { 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16 }
const IV = CryptoJS.lib.WordArray.create(
  new Uint8Array([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16])
);

/**
 * Convert a JS string to UTF-16LE WordArray (matching .NET Encoding.Unicode)
 */
function toUtf16LE(str: string): CryptoJS.lib.WordArray {
  const bytes = new Uint8Array(str.length * 2);
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    bytes[i * 2] = code & 0xff;
    bytes[i * 2 + 1] = (code >> 8) & 0xff;
  }
  return CryptoJS.lib.WordArray.create(bytes);
}

/**
 * Encrypt plaintext using AES-128-CBC, matching backend Aes.Encrypt(text, key).
 * Returns Base64-encoded ciphertext.
 */
export function aesEncrypt(plaintext: string, key: string): string {
  // Key derivation: MD5( UTF-16LE(key) ) — matches backend: hash.ComputeHash(Encoding.Unicode.GetBytes(key))
  const keyUtf16LE = toUtf16LE(key);
  const aesKey = CryptoJS.MD5(keyUtf16LE);

  // Plaintext as UTF-16LE — matches backend: Encoding.Unicode.GetBytes(text)
  const plaintextUtf16LE = toUtf16LE(plaintext);

  const encrypted = CryptoJS.AES.encrypt(plaintextUtf16LE, aesKey, {
    iv: IV,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
}

/**
 * Decrypts AES encrypted text to match .NET implementation
 */
export function aesDecrypt(encryptedBase64: string, key: string): string {
  const keyUtf16LE = toUtf16LE(key);
  const aesKey = CryptoJS.MD5(keyUtf16LE);

  const ciphertext = CryptoJS.enc.Base64.parse(encryptedBase64);
  const cipherParams = CryptoJS.lib.CipherParams.create({ ciphertext });

  const decrypted = CryptoJS.AES.decrypt(cipherParams, aesKey, {
    iv: IV,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return CryptoJS.enc.Utf16LE.stringify(decrypted).replace(/\0+$/, '');
}
